// Wires the document-kind lesson upload tab inside AddLessonModal.
// Flow per form:
//   1. User picks a file (input change or drop).
//   2. Browser POSTs filename / content_type / size to the sign
//      endpoint and gets back a key + presigned PUT URL.
//   3. Browser PUTs the file bytes directly to S3.
//   4. The hidden storage_key input gets the returned key; the
//      submit button enables; user clicks "Add lesson" and the
//      form POSTs normally to the existing AddLesson handler.

import { onceGuard } from "./bind_once.js";
import { signAndPut } from "./presigned_upload.js";

const firstBind = onceGuard();

export function initAcademyDocumentUpload(root = document) {
  const forms = root.querySelectorAll("[data-academy-document-form]");
  forms.forEach((form) => {
    if (!firstBind(form)) return;
    wireForm(form);
  });
}

function wireForm(form) {
  const signEndpoint = form.getAttribute("data-academy-document-form");
  const fileInput = form.querySelector("[data-academy-document-file]");
  const dropzone = form.querySelector("[data-academy-document-dropzone]");
  const prompt = form.querySelector("[data-academy-document-prompt]");
  const status = form.querySelector("[data-academy-document-status]");
  const keyInput = form.querySelector("[data-academy-document-key]");
  const submit = form.querySelector("[data-academy-document-submit]");
  if (!fileInput || !dropzone || !prompt || !status || !keyInput || !submit) {
    return;
  }

  let uploading = false;

  const setStatus = (text, isError = false) => {
    status.textContent = text;
    status.classList.toggle("hidden", !text);
    status.classList.toggle("text-red-600", isError);
    status.classList.toggle("text-ink-500", !isError);
  };

  const reset = () => {
    keyInput.value = "";
    submit.disabled = true;
    prompt.textContent = "Choose a file or drag one here";
    setStatus("");
  };

  reset();

  ["dragenter", "dragover"].forEach((evt) =>
    dropzone.addEventListener(evt, (e) => {
      e.preventDefault();
      dropzone.classList.add("ring-2", "ring-brand-500/40");
    }),
  );
  ["dragleave", "drop"].forEach((evt) =>
    dropzone.addEventListener(evt, (e) => {
      e.preventDefault();
      dropzone.classList.remove("ring-2", "ring-brand-500/40");
    }),
  );
  dropzone.addEventListener("drop", (event) => {
    const file = event.dataTransfer?.files?.[0];
    if (file) handle(file);
  });
  fileInput.addEventListener("change", () => {
    const file = fileInput.files?.[0];
    if (file) handle(file);
  });

  const handle = async (file) => {
    if (uploading) return;
    uploading = true;
    submit.disabled = true;
    keyInput.value = "";
    prompt.textContent = file.name;
    setStatus("Uploading…");

    try {
      const signed = await signAndPut(signEndpoint, file);
      keyInput.value = signed.key;
      submit.disabled = false;
      setStatus("Ready to attach.");
    } catch (err) {
      console.error("academy.document upload failed", err);
      // Clear the pick, so choosing the same file again retries it.
      fileInput.value = "";
      reset();
      setStatus(
        err.userMessage || "Could not upload the document. Please try again.",
        true,
      );
    } finally {
      uploading = false;
    }
  };
}
